import type { ChatConversationPage, ChatConversationView } from './views';

/** The `read` event's payload, restated so this module needs nothing from realtime. */
export interface ReadReceipt {
  conversationId: string;
  userId: string;
  readAt: string;
}

/**
 * Moves one member's read marker forward in a conversation view.
 *
 * Shared by both portals and the mobile app so the "seen" tick under a
 * message advances the same way everywhere. Only ever forward: receipts can
 * arrive out of order (two devices, a reconnect replay), and a stale one must
 * not un-read a message that has already been shown as seen.
 *
 * Returns the same object when nothing changes, so a cache write is a no-op
 * for React Query's structural sharing.
 */
export function applyReadReceipt<T extends ChatConversationView | undefined>(
  conversation: T,
  receipt: ReadReceipt,
): T {
  if (!conversation || conversation.id !== receipt.conversationId) return conversation;
  let changed = false;
  const participants = conversation.participants.map((participant) => {
    if (participant.user.id !== receipt.userId) return participant;
    if (participant.lastReadAt && participant.lastReadAt >= receipt.readAt) return participant;
    changed = true;
    return { ...participant, lastReadAt: receipt.readAt };
  });
  return changed ? { ...conversation, participants } : conversation;
}

/** {@link applyReadReceipt} across a cached conversation list. */
export function applyReadReceiptToPage<T extends ChatConversationPage | undefined>(
  page: T,
  receipt: ReadReceipt,
): T {
  if (!page) return page;
  let changed = false;
  const conversations = page.conversations.map((conversation) => {
    const next = applyReadReceipt(conversation, receipt);
    if (next !== conversation) changed = true;
    return next;
  });
  return changed ? { ...page, conversations } : page;
}
